import { Box, Button, ButtonRow, Text, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmbloomQuestionnaire } from '../../../../../interfaces/embloomQuestionnaire';
import { translateKey } from '../../../../../../translate';
import { DropDownSelectCollapse } from './DropDownSelectCollapse';
import { DropDownTileSelect } from './DropDownTileSelect';

interface IDropDownTileSelectContainerProps {
    questionnaires: IEmbloomQuestionnaire[];
    setNewProcessCustomIdentifier: (id: string | undefined) => void;
}

export interface ISelectTileProps {
    bodyText: string | null;
    id: string;
    isDecisionSupport: boolean;
    title: string;
    tooltipText: string | null;
}

type Tab = 'beslisondersteuner' | 'vragenlijst' | undefined;

export function DropDownTileSelectContainer(props: IDropDownTileSelectContainerProps) {
    const [selectedTab, setSelectedTab] = useState<Tab>();
    const [allQuestionnaires, setAllQuestionnaires] = useState<ISelectTileProps[]>([]);
    const [filteredQuestionnaires, setFilteredQuestionnaires] = useState<ISelectTileProps[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<number | undefined>();
    const [searchText, setSearchText] = useState('');
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string>();

    useEffect(() => {
        setSearchText('');
    }, [selectedTab]);

    useEffect(() => {
        onSearch();
    }, [searchText]);

    useEffect(() => {
        setAllQuestionnaires(
            props.questionnaires.map((questionnaire) => {
                return {
                    id: questionnaire.id,
                    title: questionnaire.label,
                    bodyText: questionnaire.description,
                    tooltipText: questionnaire.descriptionMedium,
                    isDecisionSupport: questionnaire.isDecisionSupport,
                };
            })
        );
    }, []);

    function getList(tab: Tab) {
        if (isTabBeslisOndersteuner(tab)) {
            return allQuestionnaires.filter((item) => item.isDecisionSupport);
        } else if (isTabVragenLijst(tab)) {
            return allQuestionnaires.filter((item) => !item.isDecisionSupport);
        }

        return [];
    }

    function getClickedElementAndSetActive(
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        button: 'beslisondersteuner' | 'vragenlijst'
    ) {
        const newSelectedValue = button === selectedTab ? undefined : button;
        setSelectedTab(newSelectedValue);
        setIsOpen(Boolean(newSelectedValue));
        changeSelectedQuestionnaireId(undefined);

        if (newSelectedValue) {
            setPosition(event.currentTarget.offsetLeft + event.currentTarget.clientWidth / 2);
        }

        setFilteredQuestionnaires(getList(newSelectedValue));
    }

    function onSearch() {
        const searchValue = searchText.toLocaleLowerCase();
        let filteredList = getList(selectedTab);
        if (searchValue) {
            filteredList = filteredList.filter(
                (item) =>
                    (item.bodyText && item.bodyText.toLocaleLowerCase().includes(searchValue)) ||
                    item.title.toLocaleLowerCase().includes(searchValue) ||
                    (item.tooltipText && item.tooltipText.toLocaleLowerCase().includes(searchValue))
            );
        }
        setFilteredQuestionnaires(filteredList);
    }

    return (
        <Box position="relative">
            <ButtonRow>
                <Button
                    color="secondary"
                    iconRight="caret-down"
                    onClick={(event) => getClickedElementAndSetActive(event, 'beslisondersteuner')}
                    variant={isTabBeslisOndersteuner(selectedTab) ? 'solid' : 'outline'}
                >
                    {`${translateKey('embloomQuestionnaires.isDecisionSupport')} ${
                        isTabBeslisOndersteuner(selectedTab) && selectedQuestionnaireId
                            ? `(${findNameById(selectedQuestionnaireId)})`
                            : ''
                    }`}
                </Button>
                <Button
                    className="automation-vragenlijst"
                    color="secondary"
                    iconRight="caret-down"
                    onClick={(event) => getClickedElementAndSetActive(event, 'vragenlijst')}
                    variant={isTabVragenLijst(selectedTab) ? 'solid' : 'outline'}
                >
                    {`${translateKey('common.questionnaire')} ${
                        isTabVragenLijst(selectedTab) && selectedQuestionnaireId
                            ? `(${findNameById(selectedQuestionnaireId)})`
                            : ''
                    }`}
                </Button>
            </ButtonRow>
            <DropDownSelectCollapse
                isOpen={isOpen}
                position={position}
            >
                <TextInput
                    onChange={(e) => {
                        setSearchText(e.target.value);
                    }}
                    onReset={() => {
                        setSearchText('');
                    }}
                    placeholder="Zoek een vragenlijst"
                    size="large"
                    suffix={{
                        labelText: 'aria labelled text',
                        icon: 'search',
                        onSuffixClick: () => onSearch(),
                    }}
                    sx={{ mb: 4 }}
                    value={searchText}
                    variant="outline"
                />
                <Text
                    mb={2}
                    size="sm"
                >
                    {translateKey('embloomQuestionnaires.pickFromList')}
                </Text>
                <DropDownTileSelect
                    onClick={(id) => {
                        setIsOpen(false);
                        changeSelectedQuestionnaireId(id);
                    }}
                    tiles={filteredQuestionnaires}
                />
            </DropDownSelectCollapse>
        </Box>
    );

    function changeSelectedQuestionnaireId(id: string | undefined) {
        props.setNewProcessCustomIdentifier(id);
        setSelectedQuestionnaireId(id);
    }

    function findNameById(id: string) {
        const selectedQuestionnaire = filteredQuestionnaires.find((el) => el.id === id);

        return selectedQuestionnaire?.title;
    }

    function isTabBeslisOndersteuner(tab: Tab) {
        return tab === 'beslisondersteuner';
    }

    function isTabVragenLijst(tab: Tab) {
        return tab === 'vragenlijst';
    }
}
